import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const catMeta: Record<string, { title: string; desc: string; keywords: string }> = {
  all: {
    title: '全部影视 - 免费在线观看 | 草民影院',
    desc: '草民影院提供全部免费电影、电视剧、综艺、动漫、纪录片在线观看，高清不卡，每日更新全网热门影视资源。',
    keywords: '免费电影,免费电视剧,免费综艺,免费动漫,全部影视,在线观看,草民影院',
  },
  movie: {
    title: '免费电影在线观看 - 高清不卡 | 草民影院',
    desc: '草民影院提供海量免费电影在线观看，包括院线大片、经典老片、热门新片，1080P高清画质，无需付费注册即可观看。',
    keywords: '免费电影,免费电影在线观看,高清电影,院线大片,在线看电影,草民影院',
  },
  'tv-series': {
    title: '免费电视剧在线观看 - 热播剧集高清不卡 | 草民影院',
    desc: '草民影院提供最新热门免费电视剧在线观看，涵盖国产剧、韩剧、美剧等，高清画质流畅不卡，追剧首选平台。',
    keywords: '免费电视剧,免费追剧,热播电视剧,国产剧,在线看电视剧,草民影院',
  },
  variety: {
    title: '免费综艺在线观看 - 热门综艺高清不卡 | 草民影院',
    desc: '草民影院提供最热门的免费综艺节目在线观看，音乐竞演、真人秀、脱口秀等精彩内容，高清画质流畅播放。',
    keywords: '免费综艺,综艺节目,热门综艺,真人秀,在线看综艺,草民影院',
  },
  anime: {
    title: '免费动漫在线观看 - 国漫日漫高清不卡 | 草民影院',
    desc: '草民影院提供免费动漫在线观看，涵盖国产动漫、日本动漫等热门番剧，高清画质流畅不卡，动漫爱好者的天堂。',
    keywords: '免费动漫,国产动漫,日本动漫,在线看动漫,热门番剧,草民影院',
  },
  documentary: {
    title: '免费纪录片在线观看 - 高清纪实 | 草民影院',
    desc: '草民影院提供高质量免费纪录片在线观看，涵盖自然、历史、科学、人文等题材，高清画质带来震撼视觉体验。',
    keywords: '免费纪录片,自然纪录片,历史纪录片,BBC纪录片,在线看纪录片,草民影院',
  },
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(slug);
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);

  const meta = catMeta[slug] || catMeta.all;
  const catName = categories.find(c => c.slug === slug)?.name || '全部';

  useEffect(() => {
    setActiveTab(slug);
    if (slug === 'all') {
      setFilteredMovies(movies);
    } else {
      setFilteredMovies(movies.filter(m => m.categorySlug === slug));
    }
  }, [slug]);

  const handleTabChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={meta.title}
        description={meta.desc}
        keywords={meta.keywords}
        canonicalUrl={`https://caominyingyuan.com/category/${slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-16 md:pt-20">
            {/* Category Header */}
            <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-white">
              <div className="max-w-screen-2xl mx-auto">
                <Breadcrumb />
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-serif mt-2">
                  {catName === '全部' ? '全部影视资源' : `免费${catName}在线观看`}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 mt-2 max-w-2xl">
                  {meta.desc}
                </p>

                {/* Tab Switcher */}
                <div className="mt-6 flex items-center gap-1 bg-background-50 rounded-full p-1 overflow-x-auto max-w-full">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleTabChange(cat.slug)}
                      className={`whitespace-nowrap px-4 md:px-6 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                        activeTab === cat.slug
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Movie Grid */}
            <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
              <div className="max-w-screen-2xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-500">
                    共找到 <strong className="text-foreground-900">{filteredMovies.length}</strong> 部作品
                  </p>
                  <div className="flex items-center gap-1 text-xs text-foreground-400">
                    <span>排序：</span>
                    <button className="px-3 py-1 rounded-full bg-primary-50 text-primary-600 font-medium cursor-pointer">默认</button>
                    <button className="px-3 py-1 rounded-full text-foreground-500 hover:text-foreground-700 cursor-pointer">最新</button>
                    <button className="px-3 py-1 rounded-full text-foreground-500 hover:text-foreground-700 cursor-pointer">评分</button>
                  </div>
                </div>

                {filteredMovies.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                    {filteredMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} size="normal" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                      <i className="ri-film-line text-foreground-400 text-2xl"></i>
                    </div>
                    <p className="text-foreground-500">暂无该分类的影视内容</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>

      {/* BreadcrumbList structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://caominyingyuan.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: catName,
              },
            ],
          }),
        }}
      />
    </>
  );
}